import React from "react";

import { useApolloClient } from "@apollo/client";

import { RadioButton } from "primereact/radiobutton";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { ListBox } from "primereact/listbox";

/** contexts */
import { useSettings } from "../../context/settings";

/** QUERIES MUTATIONS */
import { UPDATE_USER } from "./../../provider/Users/mutation";
import { USERS } from "../../provider/Users/query";

/** utils */
import { getGraphsqErros, maskPhone } from "../../utilities/functions";
import { useUser } from "../../context/users";
import { Dropdown } from "primereact/dropdown";

const Crud = () => {
  const settings = useSettings();
  const apollo = useApolloClient();

  const user = useUser();
  const franchise_id = user?.getUser()?.franchise?.id;
  const acting_region_id = user?.getUser()?.actingRegion?.id;

  let emptyRecord: iUser = {
    name: "",
    phone: "",
    email: "",
    is_root: false,
  };

  const [records, setRecords] = React.useState<any[]>([]);
  const [franchises, setFranchises] = React.useState<iFranchise[]>([]);
  const [regions, setRegions] = React.useState<iRegion[]>([]);
  const [region, setRegion] = React.useState<any>(null);
  const [record, setRecord] = React.useState<iUser>();
  const [franchise, setFranchise] = React.useState<any>(null);

  const [loading, setLoading] = React.useState<boolean>(false);
  const [recordDialog, setRecordDialog] = React.useState<boolean>(false);
  const [load, setLoad] = React.useState(true);
  const [reload, setReload] = React.useState<boolean>(false);

  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const [globalFilter, setGlobalFilter] = React.useState<any>(null);
  const toast = React.useRef<any>(null);
  const dt = React.useRef<any>(null);

  const loadRecords = (cleanRecord: boolean = false) => {
    setLoading(true);

    const filter: any = {};

    if (franchise_id) filter.franchise_id = Number(franchise_id);

    if (acting_region_id) filter.acting_region_id = Number(acting_region_id);

    if (franchise?.code) filter.franchise_id = Number(franchise.code);

    if (region?.code) filter.acting_region_id = Number(region.code);

    apollo
      .query({
        query: USERS,
        variables: filter,
        fetchPolicy: "no-cache",
      })
      .then(response => {
        const data = response.data.users;
        setFranchises(response.data.franchises);
        setRegions(response.data.regions);
        if (data) {
          setRecords(data);
        }
        setLoading(false);
        setLoad(false);
        setReload(false);
      })
      .catch(error => {
        console.log('USERS error: ', JSON.stringify(error));
        setRecords([]);
        settings?.setErrorMessage(getGraphsqErros(error));
        settings?.setVisibleError(true);
        setLoading(false);
        setLoad(false);
        setReload(false);
      });
  };

  const hideDialog = () => {
    setSubmitted(false);
    setRecordDialog(false);
  };

  const saveRecord = () => {
    setSubmitted(true);

    if (!record?.name || !record.phone) return;

    const _record: iUser = { ...record };
    delete _record.__typename;
    delete _record.email;
    delete _record.token;
    delete _record.token_expires_at;
    delete _record.push_token;
    _record.franchise_id = franchise?.code ? parseInt(franchise.code) : undefined;

    if (franchise_id) {
      _record.franchise_id = Number(franchise_id);
    }

    if (region) {
      _record.acting_region_id = Number(region.code);
    }

    if (_record.acting_region_id === 0) delete _record.acting_region_id;

    delete _record.__typename;
    delete _record.franchise;
    delete _record.actingRegion;

    _record.id = parseInt(`${_record.id}`);

    setLoading(true);

    apollo
      .mutate({
        mutation: UPDATE_USER,
        variables: {
          data: {
            ..._record,
          },
        },
      })
      .then(response => {
        setLoading(false);

        toast.current.show({ severity: "success", summary: "Sucesso", detail: "Registro atualizado.", life: 3000 });

        loadRecords(true);
        setRecordDialog(false);
        setRecord(emptyRecord);
        setFranchise(null);
        setRegion(null);
      })
      .catch(error => {
        console.log('UPDATE_USER error: ', JSON.stringify(error));
        setLoading(false);
        settings.setErrorMessage(getGraphsqErros(error));
        settings.setVisibleError(true);
      });
  };

  const editRecord = (record: iUser) => {
    const _record: iUser = { ...record };

    setFranchise({ code: _record.franchise?.id, name: _record.franchise?.name });
    setRegion({ code: _record.actingRegion?.id, name: _record.actingRegion?.name });
    setRecord({ ...record });
    setRecordDialog(true);
  };

  const onInputChange = (e: any, name: string) => {
    let val = (e.target && e.target.value) || "";

    if (name === "phone") {
      val = maskPhone(val);
    }

    let _record: any = { ...record };
    _record[name] = val;

    setRecord(_record);
  };

  const onRoleChange = (e: any) => {
    let _record: iUser = { ...record };
    _record.is_root = e.value;
    setRecord(_record);
  };

  const codeBodyTemplate = (rowData: iUser) => {
    return (
      <>
        <span className="p-column-title">Uid</span>
        {rowData.uid}
      </>
    );
  };

  const nameBodyTemplate = (rowData: iUser) => {
    return (
      <>
        <span className="p-column-title">Nome</span>
        {rowData.name}
      </>
    );
  };

  const franchiseBodyTemplate = (rowData: iUser) => {
    return (
      <>
        <span className="p-column-title">Franquia</span>
        {rowData.franchise?.name}
      </>
    );
  };

  const actingRegionBodyTemplate = (rowData: iUser) => {
    return (
      <>
        <span className="p-column-title">Região</span>
        {rowData.actingRegion?.name}
      </>
    );
  };

  const phoneBodyTemplate = (rowData: iUser) => {
    return (
      <>
        <span className="p-column-title">Telefone</span>
        {rowData.phone}
      </>
    );
  };

  const adminBodyTemplate = (rowData: iUser) => {
    return (
      <>
        <span className="p-column-title">Admin</span>
        <span className={`product-badge status-${!rowData.is_root ? "disabled" : "actived"}`}>{rowData.is_root ? "Sim" : "Não"}</span>
      </>
    );
  };

  const actionBodyTemplate = (rowData: any) => {
    return (
      <div className="actions">
        <Button icon="pi pi-pencil" className="p-button-rounded p-button-info mr-2" onClick={() => editRecord(rowData)} />
      </div>
    );
  };

  const header = (
    <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
      <h5 className="m-0">{loading ? "Carregando usuários" : "Usuários"}</h5>
      <span className="block mt-2 md:mt-0 p-input-icon-left">
        <i className="pi pi-search" />
        <InputText className="min-w-full" type="search" onInput={e => setGlobalFilter(e.currentTarget.value)} placeholder="Procurar..." />
      </span>
    </div>
  );

  const recordDialogFooter = (
    <>
      <Button label="Cancelar" icon="pi pi-times" disabled={loading} className="p-button-text text-red-500" onClick={hideDialog} />
      <Button label={loading ? "Salvando..." : "Salvar"} disabled={loading} icon="pi pi-check" className="p-button-text" onClick={saveRecord} />
    </>
  );

  React.useEffect(() => {
    loadRecords(true);
  }, [region?.code]);

  React.useEffect(() => {
    if (franchise?.code){
      setRegion(null);
      setReload(true);
      loadRecords(true);
    }
  }, [franchise?.code]);

  return (
    <div className="grid crud-demo">
      {franchise_id && acting_region_id ? null : (
        load ? (
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
        ) : (
          <>
            <div className="card">
              <div className="flex flex-column md:flex-row flex-wrap">
                { !franchise_id ? (
                  <div className="flex flex-column md:flex-row flex-wrap">
                    <label htmlFor="name">Franquia</label>
                    <div className="field col-12 md:col-12">
                      <Dropdown
                        value={franchise}
                        onChange={e => setFranchise(e.value)}
                        options={franchises.map(i => ({ code: i.id, name: i.name }))}
                        optionLabel="name"
                        placeholder="Selecione uma Franquia"
                      />
                    </div>
                  </div>
                ) : null }
  
  
                { !acting_region_id ? (
                  <div className="flex flex-column md:flex-row flex-wrap">
                    <label htmlFor="name">Região</label>
                    <div className="field col-12 md:col-12">
                      {reload ? (
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
                      ) : (
                        <Dropdown
                          value={region}
                          onChange={e => setRegion(e.value)}
                          options={regions.map(i => ({ code: i.id, name: i.name }))}
                          optionLabel="name"
                          placeholder="Selecione uma Região"
                        />
                      )}
                    </div>
                  </div>
                ) : null }
              </div>
            </div>
          </>
        )
      )}
      <div className="col-12">
        <div className="card">
          <Toast ref={toast} />

          <DataTable
            ref={dt}
            value={records.filter(i => i.role !== "ROLE_MASTER")}
            dataKey="id"
            paginator
            stripedRows={true}
            rowHover
            showGridlines
            rows={10}
            rowsPerPageOptions={[5, 10, 25]}
            className="datatable-responsive"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} registros"
            globalFilter={globalFilter}
            emptyMessage="Nenhum registro encontrado"
            header={header}
            responsiveLayout="scroll">
            <Column field="matricula" header="Matrícula" sortable body={codeBodyTemplate} headerStyle={{}} />
            <Column field="name" header="Nome" sortable body={nameBodyTemplate} headerStyle={{}} />
            { !franchise_id ? (
              <Column field="franchise.name" header="Franquia" sortable body={franchiseBodyTemplate} headerStyle={{}} />
            ) : null}
            { !acting_region_id ? (
              <Column field="actingRegion.name" header="Região" sortable body={actingRegionBodyTemplate} headerStyle={{}} />
            ) : null }
            <Column field="phone" header="Telefone" body={phoneBodyTemplate} sortable headerStyle={{}} />
            <Column field="is_root" header="Admin" body={adminBodyTemplate} sortable headerStyle={{}} />
            <Column header="Ações" className="flex flex-row align-items-center justify-content-end" body={actionBodyTemplate} />
          </DataTable>

          {/* Modal adicionar editar */}
          <Dialog
            visible={recordDialog}
            style={{ width: "550px" }}
            header={record?.id ? "Detalhes do Usuário" : "Novo Usuário"}
            modal
            className="p-fluid"
            footer={recordDialogFooter}
            onHide={hideDialog}>
            <div className="flex flex-column md:flex-row ">
              <div className="field col-12 md:col-12">
                <label htmlFor="name">* Nome</label>
                <InputText
                  id="name"
                  value={record?.name}
                  onChange={e => onInputChange(e, "name")}
                  required
                  autoFocus
                  className={classNames("p-3", { "p-invalid": submitted && !record?.name })}
                />
                {submitted && !record?.name && <small className="p-invalid">Nome é obrigatório.</small>}
              </div>
            </div>
            { !franchise_id ? (
              <div className="flex flex-column md:flex-row flex-wrap">
                <div className="field col-12 md:col-12">
                  <label htmlFor="name">Franquia</label>
                  <Dropdown
                    value={franchise}
                    onChange={e => setFranchise(e.value)}
                    options={franchises.map(i => ({ code: i.id, name: i.name }))}
                    optionLabel="name"
                    placeholder="Selecione uma Franquia"
                  />
                </div>
              </div>
            ) : null }
            { !acting_region_id ? (
              <div className="flex flex-column md:flex-row flex-wrap">
                <div className="field col-12 md:col-12">
                  <label htmlFor="name">Região</label>
                  <Dropdown
                    value={region}
                    onChange={e => setRegion(e.value)}
                    options={regions.map(i => ({ code: i.id, name: i.name }))}
                    optionLabel="name"
                    placeholder="Selecione uma Região"
                  />
                </div>
              </div>
            ) : null }
            <div className="flex flex-column md:flex-row ">
              <div className="field col-12 md:col-12">
                <label htmlFor="phone">* Telefone</label>
                <InputText
                  id="phone"
                  disabled
                  readOnly
                  value={record?.phone}
                  autoComplete={"new-password"}
                  className={classNames("p-3", { "p-invalid": submitted && !record?.phone })}
                />
                {submitted && !record?.phone && <small className="p-invalid">Telefone é obrigatório.</small>}
              </div>
            </div>

            <div className="flex flex-column md:flex-row ">
              <div className="field col-12 md:col-12">
                <label className="mb-3">* Administrador</label>
                <div className="formgrid grid">
                  <div className="field-radiobutton col-4">
                    <RadioButton inputId="role1" name="is_root" value={true} onChange={onRoleChange} checked={record?.is_root} />
                    <label htmlFor="role1">Sim</label>
                  </div>
                  <div className="field-radiobutton col-4">
                    <RadioButton inputId="role2" name="is_root" value={false} onChange={onRoleChange} checked={!record?.is_root} />
                    <label htmlFor="role2">Não</label>
                  </div>
                </div>
              </div>
            </div>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default Crud;
